import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  LogOut, Trophy, Users, ArrowLeft, RefreshCw, GripVertical,
  Plus, ChevronDown, ChevronUp, Swords, CalendarDays, Flag, Download, Warehouse, Printer, Shirt, ExternalLink, AlertTriangle,
  MessageSquare, Mail, Phone, Send, Search, Check
} from "lucide-react";
import IncidentReportPage from "./IncidentReportPage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";


const SESSION_COLORS = {
  Full: "bg-emerald-100 text-emerald-800 border-emerald-300",
  "6Wk": "bg-blue-100 text-blue-800 border-blue-300",
  HS1: "bg-amber-100 text-amber-800 border-amber-300",
  HS2: "bg-orange-100 text-orange-800 border-orange-300",
  F20: "bg-purple-100 text-purple-800 border-purple-300",
  F25: "bg-pink-100 text-pink-800 border-pink-300",
  F30: "bg-rose-100 text-rose-800 border-rose-300",
};

const TEAM_COLORS_BOYS = {
  "Wendy's": "#e11d48", "Taco Bell": "#7c3aed", "Subway": "#16a34a",
  "Panera": "#ca8a04", "Chick-fil-A": "#dc2626", "Chipotle": "#b45309",
};
const TEAM_COLORS_GIRLS = {
  "McDonalds": "#dc2626", "Pizza Hut": "#ea580c", "Burger King": "#ca8a04",
  "KFC": "#dc2626", "Shake Shack": "#16a34a", "Dominos": "#2563eb",
};

export default function AthleticsDashboard({ onLogout }) {
  const [view, setView] = useState("landing");
  const [subView, setSubView] = useState(null);
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // Detect shed-only login: athletics token with `view: "shed"` claim.
  // These users see ONLY the Athletics Shed card on the landing.
  const isShedOnly = (() => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.view === "shed";
    } catch { return false; }
  })();

  // Auto-bounce shed-only users straight into the shed if they somehow end
  // up on landing — but still allow them to back out (Logout) from the shed.
  // (We keep the landing render with only the shed card so the back-from-shed
  // flow doesn't loop.)

  if (view === "landing") {
    const allCards = [
      { id: "boys-teams", label: "3rd & 4th Grade Boys Intramurals", icon: Users, color: "blue" },
      { id: "girls-teams", label: "3rd & 4th Grade Girls Intramurals", icon: Users, color: "rose" },
      { id: "boys-schedule", label: "Boys Schedule & Scorecard", icon: CalendarDays, color: "emerald" },
      { id: "girls-schedule", label: "Girls Schedule & Scorecard", icon: CalendarDays, color: "teal" },
      { id: "playoffs", label: "Playoffs", icon: Swords, color: "amber" },
      { id: "final-standings", label: "Final Standings", icon: Trophy, color: "violet" },
      { id: "shed", label: "Athletics Shed", subtitle: "Equipment Sign-Out Log", icon: Warehouse, color: "stone" },
      { id: "messages", label: "Staff Messages", subtitle: "SMS & Email to Athletics Staff", icon: MessageSquare, color: "indigo" },
      { id: "incident-report", label: "File Incident Report", subtitle: "Medical / Behavioral", icon: AlertTriangle, color: "red" },
    ];
    const cards = isShedOnly ? allCards.filter(c => c.id === "shed") : allCards;
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50/40 to-white">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-blue-600" />
            <div>
              <h1 className="text-sm font-bold text-blue-800">{isShedOnly ? "Athletics Shed" : "Athletics Dashboard"}</h1>
              <p className="text-[10px] text-muted-foreground">{isShedOnly ? "Equipment Sign-Out" : "Mariner Intramurals"}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="athletics-logout">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </header>
        <div className="px-4 py-8 max-w-lg mx-auto space-y-4">
          {!isShedOnly && (
            <div className="bg-red-50 border-2 border-red-500 rounded-2xl p-4" data-testid="athletics-non-negotiable-banner">
              <p className="text-xs font-bold text-red-900 uppercase tracking-wide mb-1">Non-Negotiable: Daily score entry</p>
              <p className="text-sm text-red-800">
                Mariner Intramural scores (boys and girls) must be entered the same day a match is played. Standings, playoffs, and final rankings depend on it.
              </p>
            </div>
          )}
          {cards.map(card => (
            <button key={card.id} onClick={() => { setView(card.id); setSubView(null); }}
              className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 bg-${card.color}-50 border-${card.color}-200 hover:border-${card.color}-400 hover:shadow-lg transition-all active:scale-[0.98]`}
              data-testid={`athletics-card-${card.id}`}
            >
              <div className={`w-12 h-12 rounded-full bg-${card.color}-100 flex items-center justify-center border border-${card.color}-200`}>
                <card.icon className={`w-6 h-6 text-${card.color}-600`} />
              </div>
              <div>
                <span className={`text-sm font-bold text-${card.color}-800`}>{card.label}</span>
                {card.subtitle && <p className={`text-[10px] text-${card.color}-500`}>{card.subtitle}</p>}
              </div>
            </button>
          ))}
          {!isShedOnly && (
            <a
              href="https://rrdcstaffsnapshot.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 bg-emerald-50 border-emerald-200 hover:border-emerald-400 hover:shadow-lg transition-all active:scale-[0.98]"
              data-testid="athletics-card-staff-snapshot"
            >
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center border border-emerald-200">
                <Shirt className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1 flex items-center justify-between">
                <span className="text-sm font-bold text-emerald-800">Staff Snapshot</span>
                <ExternalLink className="w-4 h-4 text-emerald-600" />
              </div>
            </a>
          )}
        </div>
      </div>
    );
  }

  if (view === "boys-teams" || view === "girls-teams") {
    const division = view === "boys-teams" ? "boys" : "girls";
    return <TeamSorterView division={division} headers={headers} onBack={() => setView("landing")} isEditable={true} />;
  }

  if (view === "boys-schedule" || view === "girls-schedule") {
    const defaultTab = view === "boys-schedule" ? "boys" : "girls";
    return <ScorecardView headers={headers} onBack={() => setView("landing")} isEditable={true} defaultTab={defaultTab} />;
  }

  if (view === "playoffs") {
    if (!subView) {
      return (
        <div className="min-h-screen bg-background">
          <SimpleHeader title="Playoffs" onBack={() => setView("landing")} onLogout={onLogout} />
          <div className="px-4 py-8 max-w-lg mx-auto space-y-4">
            {[
              { id: "boys", label: "Boys Playoffs", color: "blue" },
              { id: "girls", label: "Girls Playoffs", color: "rose" },
            ].map(card => (
              <button key={card.id} onClick={() => setSubView(card.id)}
                className={`w-full flex items-center gap-4 p-5 rounded-2xl border-2 bg-${card.color}-50 border-${card.color}-200 hover:border-${card.color}-400 hover:shadow-lg transition-all`}
                data-testid={`playoffs-card-${card.id}`}
              >
                <Swords className={`w-6 h-6 text-${card.color}-600`} />
                <span className={`text-sm font-bold text-${card.color}-800`}>{card.label}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }
    return <PlayoffsView division={subView} headers={headers} onBack={() => setSubView(null)} isEditable={true} />;
  }

  if (view === "final-standings") {
    return <FinalStandingsView headers={headers} onBack={() => setView("landing")} />;
  }

  if (view === "shed") {
    return <AthleticsShedView onBack={() => setView("landing")} />;
  }

  if (view === "messages") {
    return <AthleticsMessagesView headers={headers} onBack={() => setView("landing")} onLogout={onLogout} />;
  }

  if (view === "incident-report") {
    return (
      <IncidentReportPage
        group={{ code: "ATHLETICS", name: "Athletics" }}
        mode="athletics"
        onBack={() => setView("landing")}
      />
    );
  }

  return null;
}

function SimpleHeader({ title, subtitle, onBack, onLogout, children }) {
  return (
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="px-2"><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-sm font-bold">{title}</h1>
          {subtitle && <p className="text-[10px] text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {children}
      </div>
    </header>
  );
}

// ═══════════════════════════════════════
// TEAM SORTER
// ═══════════════════════════════════════

function TeamSorterView({ division, headers, onBack, isEditable }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState(false);
  const [tab, setTab] = useState("teams");
  const [dragOverTeam, setDragOverTeam] = useState(null);
  const [assistants, setAssistants] = useState({ assistants: [], by_team: {} });
  const [heads, setHeads] = useState({ heads: [], by_activity: {} });
  const [uploadingAssts, setUploadingAssts] = useState(false);
  const [uploadingHeads, setUploadingHeads] = useState(false);
  const teamColors = division === "boys" ? TEAM_COLORS_BOYS : TEAM_COLORS_GIRLS;

  const fetchTeams = useCallback(async () => {
    try {
      const [tRes, aRes, hRes] = await Promise.all([
        axios.get(`${API}/intramurals/${division}/teams`, { headers }),
        axios.get(`${API}/intramurals/${division}/assistants`, { headers }),
        axios.get(`${API}/intramurals/${division}/heads`, { headers }),
      ]);
      setData(tRes.data);
      setAssistants(aRes.data || { assistants: [], by_team: {} });
      setHeads(hRes.data || { heads: [], by_activity: {} });
    } catch { toast.error("Failed to load teams"); }
    setLoading(false);
  }, [division]);

  const uploadAssistantsCsv = async (file) => {
    if (!file) return;
    setUploadingAssts(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/intramurals/${division}/assistants/upload`, fd, { headers });
      const { inserted, skipped, unknown_teams, valid_teams } = res.data || {};
      if (skipped && skipped.length) {
        toast.warning(`Uploaded ${inserted}. Skipped ${skipped.length} (unknown teams: ${unknown_teams.join(", ")}). Valid: ${valid_teams.join(", ")}`);
      } else {
        toast.success(`Uploaded ${inserted} assistant${inserted !== 1 ? "s" : ""}`);
      }
      fetchTeams();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploadingAssts(false);
  };

  const uploadHeadsCsv = async (file) => {
    if (!file) return;
    setUploadingHeads(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/intramurals/${division}/heads/upload`, fd, { headers });
      toast.success(`Uploaded ${res.data?.inserted || 0} head${(res.data?.inserted || 0) !== 1 ? "s" : ""}`);
      fetchTeams();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploadingHeads(false);
  };

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const handleSort = async () => {
    if (!window.confirm("This will re-sort all campers and override manual changes. Are you sure?")) return;
    setSorting(true);
    try {
      const res = await axios.post(`${API}/intramurals/${division}/sort`, {}, { headers });
      setData(res.data);
      toast.success("Teams sorted!");
    } catch (err) { toast.error(err.response?.data?.detail || "Sort failed"); }
    setSorting(false);
  };

  const handleDrop = async (toTeam, e) => {
    e.preventDefault();
    setDragOverTeam(null);
    const camperId = e.dataTransfer.getData("camper_id");
    const fromTeam = e.dataTransfer.getData("from_team");
    if (!camperId || !fromTeam || fromTeam === toTeam) return;
    try {
      await axios.put(`${API}/intramurals/${division}/move`, {
        camper_id: camperId, from_team: fromTeam, to_team: toTeam
      }, { headers });
      fetchTeams();
    } catch { toast.error("Move failed"); }
  };

  const teams = data?.teams || {};
  const teamNames = Object.keys(teams);
  const title = division === "boys" ? "Boys Intramurals" : "Girls Intramurals";

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  // Build roster view
  const allCampers = [];
  for (const [team, members] of Object.entries(teams)) {
    for (const c of members) {
      allCampers.push({ ...c, team });
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <SimpleHeader title={title} subtitle={data?.sorted ? `Sorted ${teamNames.length} teams` : "Not sorted yet"} onBack={onBack}>
        {isEditable && (
          <Button size="sm" className="h-8 text-xs" onClick={handleSort} disabled={sorting} data-testid="sort-teams-btn">
            {sorting ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            {data?.sorted ? "Re-Sort" : "Sort Teams"}
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="intramural-pdf-btn"
          onClick={async () => {
            try {
              const res = await fetch(`${API}/intramurals/${division}/export/pdf`, { headers });
              const blob = await res.blob();
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${division}_intramurals.pdf`; a.click();
            } catch { toast.error("PDF export failed"); }
          }}>
          <Download className="w-3 h-3 mr-1" /> PDF
        </Button>
      </SimpleHeader>

      {/* Tabs */}
      <div className="border-b px-4">
        <div className="flex gap-4">
          <button onClick={() => setTab("teams")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "teams" ? "border-blue-600 text-blue-700" : "border-transparent text-muted-foreground"}`}>Team View</button>
          <button onClick={() => setTab("roster")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "roster" ? "border-blue-600 text-blue-700" : "border-transparent text-muted-foreground"}`}>Roster View</button>
          <button onClick={() => setTab("heads")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "heads" ? "border-blue-600 text-blue-700" : "border-transparent text-muted-foreground"}`} data-testid="tab-heads">
            Heads / Stations
            {heads.heads.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[9px]">{heads.heads.length}</span>}
          </button>
        </div>
      </div>

      {/* CSV upload bar — admin/athletics only */}
      {isEditable && (
        <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2 items-center text-xs">
          <span className="font-semibold text-muted-foreground">CSV Upload:</span>
          <label className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border bg-white hover:bg-blue-50 cursor-pointer" data-testid="upload-asst-btn">
            <Download className="w-3 h-3 rotate-180" />
            {uploadingAssts ? "Uploading…" : "Assistants → Teams"}
            <input type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadAssistantsCsv(f); }} />
          </label>
          <label className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border bg-white hover:bg-blue-50 cursor-pointer" data-testid="upload-heads-btn">
            <Download className="w-3 h-3 rotate-180" />
            {uploadingHeads ? "Uploading…" : "Heads → Game/Activity"}
            <input type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadHeadsCsv(f); }} />
          </label>
          <span className="text-[10px] text-muted-foreground ml-1">
            Columns — Assistants: <code>Last Name, First Name, Team</code> · Heads: <code>Last Name, First Name, Game/Activity</code>
          </span>
        </div>
      )}

      {tab === "teams" && (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {teamNames.map(teamName => {
            const members = teams[teamName] || [];
            const color = teamColors[teamName] || "#666";
            const byGroup = {};
            members.forEach(c => { byGroup[c.group_code] = byGroup[c.group_code] || []; byGroup[c.group_code].push(c); });
            const sessionCounts = {};
            members.forEach(c => { sessionCounts[c.session_label] = (sessionCounts[c.session_label] || 0) + 1; });

            return (
              <Card key={teamName}
                className={`overflow-hidden transition-all ${dragOverTeam === teamName ? "ring-2 ring-blue-500 bg-blue-50/30" : ""}`}
                onDragOver={isEditable ? (e) => { e.preventDefault(); setDragOverTeam(teamName); } : undefined}
                onDragLeave={isEditable ? () => setDragOverTeam(null) : undefined}
                onDrop={isEditable ? (e) => handleDrop(teamName, e) : undefined}
                data-testid={`team-card-${teamName.replace(/[^a-zA-Z]/g, "")}`}
              >
                <div className="p-2 text-center text-white text-xs font-bold" style={{ backgroundColor: color }}>
                  {teamName}
                </div>
                <CardContent className="p-2 space-y-1">
                  {/* Assigned assistants (uploaded via CSV) */}
                  <div className="text-[10px] border-b pb-1 mb-1 space-y-0.5">
                    {(assistants.by_team[teamName] || []).length === 0 ? (
                      <div className="text-muted-foreground italic">No assistants assigned</div>
                    ) : (
                      (assistants.by_team[teamName] || []).map((a) => (
                        <div key={a.id} className="text-stone-700 font-medium" data-testid={`asst-${a.id}`}>
                          {a.last_name}, {a.first_name}
                        </div>
                      ))
                    )}
                  </div>

                  {Object.keys(byGroup).sort().map(gc => (
                    <div key={gc}>
                      <div className="text-[10px] font-bold text-muted-foreground mt-1">{gc}</div>
                      {byGroup[gc].map(c => (
                        <div key={c.id}
                          draggable={isEditable}
                          onDragStart={isEditable ? (e) => { e.dataTransfer.setData("camper_id", c.id); e.dataTransfer.setData("from_team", teamName); e.dataTransfer.effectAllowed = "move"; } : undefined}
                          className={`flex items-center justify-between py-1 px-1.5 rounded text-[11px] hover:bg-muted/50 border border-transparent hover:border-muted ${isEditable ? "cursor-grab active:cursor-grabbing" : ""} ${c.manual_move ? "bg-yellow-50 border-yellow-200" : ""}`}
                          data-testid={`camper-${c.id}`}
                        >
                          <span className="truncate">{isEditable && <GripVertical className="w-2.5 h-2.5 inline mr-0.5 text-muted-foreground" />}{c.name}</span>
                          <Badge variant="outline" className={`text-[8px] px-1 py-0 ml-1 ${SESSION_COLORS[c.session_label] || ""}`}>{c.session_label}</Badge>
                        </div>
                      ))}
                    </div>
                  ))}

                  <div className="border-t pt-1 mt-1 text-[10px] text-muted-foreground text-center">
                    <div className="font-semibold">{members.length} campers</div>
                    <div>{Object.entries(sessionCounts).map(([k, v]) => `${k}: ${v}`).join(" | ")}</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {tab === "roster" && (
        <div className="p-4 max-w-4xl mx-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 px-2 font-semibold">Name</th>
                <th className="py-2 px-2 font-semibold">Group</th>
                <th className="py-2 px-2 font-semibold">Session</th>
                <th className="py-2 px-2 font-semibold">Team</th>
              </tr>
            </thead>
            <tbody>
              {allCampers.sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                <tr key={c.id} className="border-b hover:bg-muted/30">
                  <td className="py-1.5 px-2">{c.name}</td>
                  <td className="py-1.5 px-2">{c.group_code}</td>
                  <td className="py-1.5 px-2"><Badge variant="outline" className={`text-[9px] ${SESSION_COLORS[c.session_label] || ""}`}>{c.session_label}</Badge></td>
                  <td className="py-1.5 px-2"><span className="font-medium" style={{ color: teamColors[c.team] }}>{c.team}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "heads" && (
        <div className="p-4 max-w-3xl mx-auto" data-testid="heads-tab-content">
          {heads.heads.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No heads uploaded yet. {isEditable ? "Use the CSV upload above (Last Name, First Name, Game/Activity)." : "Ask an admin to upload the activity heads."}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.keys(heads.by_activity).sort().map(activity => (
                <Card key={activity} data-testid={`head-card-${activity.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <div className="p-2 text-center text-white text-xs font-bold bg-slate-700">{activity}</div>
                  <CardContent className="p-2 space-y-0.5">
                    {(heads.by_activity[activity] || []).map(h => (
                      <div key={h.id} className="text-xs text-stone-700">
                        <span className="font-medium">{h.last_name}, {h.first_name}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// SCORECARD
// ═══════════════════════════════════════

function ScorecardView({ headers, onBack, isEditable, defaultTab }) {
  const [tab, setTab] = useState(defaultTab || "boys");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSchedule = useCallback(async (div) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/intramurals/${div}/schedule`, { headers });
      setData(res.data);
    } catch { toast.error("Failed to load schedule"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSchedule(tab); }, [tab, fetchSchedule]);

  const pendingScores = useRef({});

  const handleScoreChange = async (key, field, value) => {
    // Track pending changes in ref (always current)
    pendingScores.current[key] = { ...pendingScores.current[key], [field]: value };
    // Optimistic update
    setData(prev => {
      if (!prev) return prev;
      const updated = { ...prev, schedule: prev.schedule.map(week => ({
        ...week, games: week.games.map(g => g.key === key ? { ...g, [field]: value } : g)
      }))};
      return updated;
    });
  };

  const handleScoreBlur = async (key) => {
    // Read from ref (always has latest values) merged with current data
    const game = data?.schedule?.flatMap(w => w.games).find(g => g.key === key);
    if (!game) return;
    const pending = pendingScores.current[key] || {};
    const score1 = pending.score1 !== undefined ? pending.score1 : game.score1;
    const score2 = pending.score2 !== undefined ? pending.score2 : game.score2;
    try {
      await axios.put(`${API}/intramurals/${tab}/score`, { key, score1, score2 }, { headers });
    } catch { toast.error("Failed to save score"); }
    delete pendingScores.current[key];
    fetchSchedule(tab);
  };

  const teamColors = tab === "boys" ? TEAM_COLORS_BOYS : TEAM_COLORS_GIRLS;

  return (
    <div className="min-h-screen bg-background">
      <SimpleHeader title="Scorecard" subtitle="6-Week Round Robin" onBack={onBack} />

      <div className="border-b px-4">
        <div className="flex gap-4">
          <button onClick={() => setTab("boys")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "boys" ? "border-blue-600 text-blue-700" : "border-transparent text-muted-foreground"}`}>Boys Schedule</button>
          <button onClick={() => setTab("girls")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "girls" ? "border-rose-600 text-rose-700" : "border-transparent text-muted-foreground"}`}>Girls Schedule</button>
        </div>
      </div>

      {loading ? <div className="text-center py-10 text-muted-foreground">Loading...</div> : data && (
        <div className="p-4 max-w-5xl mx-auto space-y-6">
          {/* Points Legend */}
          <div className="flex items-center justify-center gap-6 py-2 px-4 rounded-lg bg-slate-50 border border-slate-200 text-xs font-semibold" data-testid="points-legend">
            <span className="text-emerald-700">Win = 10 pts</span>
            <span className="text-slate-500">|</span>
            <span className="text-amber-700">Tie = 5 pts</span>
            <span className="text-slate-500">|</span>
            <span className="text-red-700">Loss = 3 pts</span>
          </div>

          {/* Standings */}
          <Card>
            <CardContent className="pt-4">
              <h3 className="text-xs font-bold mb-2">Standings</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                {(data.standings || []).map((s, i) => (
                  <div key={s.team} className="text-center p-2 rounded-lg bg-muted/30 border">
                    <div className="text-[10px] font-bold" style={{ color: teamColors[s.team] }}>{s.team}</div>
                    <div className="text-lg font-bold">{s.points}</div>
                    <div className="text-[9px] text-muted-foreground">{s.w || 0}W-{s.l || 0}L-{s.t || 0}T</div>
                    <div className="text-[9px] text-muted-foreground">#{i + 1}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Schedule */}
          {(data.schedule || []).map(week => (
            <Card key={week.week}>
              <CardContent className="pt-4">
                <h3 className="text-xs font-bold mb-3">Week {week.week}</h3>
                <div className="space-y-3">
                  {week.games.map(game => (
                    <div key={game.key} className="flex items-center gap-2 text-xs flex-wrap">
                      <span className="font-semibold text-right min-w-[70px] flex-shrink-0" style={{ color: teamColors[game.team1] }}>{game.team1}</span>
                      <span className="text-muted-foreground text-[10px]">vs.</span>
                      <span className="font-semibold min-w-[70px] flex-shrink-0" style={{ color: teamColors[game.team2] }}>{game.team2}</span>
                      <span className="text-muted-foreground text-[10px] flex-1 min-w-[80px]">{game.activity}</span>
                      <span className="text-muted-foreground text-[10px] min-w-[70px]">{game.location}</span>
                      {isEditable ? (
                        <div className="flex items-center gap-1">
                          <Input type="number" className="w-14 h-7 text-xs text-center" value={game.score1 ?? ""}
                            onChange={e => handleScoreChange(game.key, "score1", e.target.value)}
                            onBlur={() => handleScoreBlur(game.key)}
                            placeholder="Score"
                            data-testid={`score-${game.key}-1`} />
                          <Input type="number" className="w-14 h-7 text-xs text-center" value={game.score2 ?? ""}
                            onChange={e => handleScoreChange(game.key, "score2", e.target.value)}
                            onBlur={() => handleScoreBlur(game.key)}
                            placeholder="Score"
                            data-testid={`score-${game.key}-2`} />
                          {game.pts1 != null && game.pts2 != null && (
                            <span className="text-[9px] font-bold text-slate-500 whitespace-nowrap ml-1" data-testid={`pts-${game.key}`}>
                              ({game.pts1}-{game.pts2})
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="w-14 h-7 text-xs text-center font-bold inline-flex items-center justify-center bg-muted/30 rounded">{game.score1 ?? "—"}</span>
                          <span className="w-14 h-7 text-xs text-center font-bold inline-flex items-center justify-center bg-muted/30 rounded">{game.score2 ?? "—"}</span>
                          {game.pts1 != null && game.pts2 != null && (
                            <span className="text-[9px] font-bold text-slate-500 whitespace-nowrap ml-1">
                              ({game.pts1}-{game.pts2})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// PLAYOFFS
// ═══════════════════════════════════════

function PlayoffsView({ division, headers, onBack, isEditable }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [standings, setStandings] = useState([]);
  const teamColors = division === "boys" ? TEAM_COLORS_BOYS : TEAM_COLORS_GIRLS;

  const fetchPlayoffs = useCallback(async () => {
    try {
      const [playoffRes, standingsRes] = await Promise.all([
        axios.get(`${API}/intramurals/${division}/playoffs`, { headers }),
        axios.get(`${API}/intramurals/${division}/schedule`, { headers }),
      ]);
      setData(playoffRes.data);
      setStandings(standingsRes.data.standings || []);
    } catch { toast.error("Failed to load playoffs"); }
    setLoading(false);
  }, [division]);

  useEffect(() => { fetchPlayoffs(); }, [fetchPlayoffs]);

  const save = async (games) => {
    setSaving(true);
    try {
      await axios.put(`${API}/intramurals/${division}/playoffs`, { games }, { headers });
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const addGame = () => {
    const games = [...(data?.games || []), { team1: "", team2: "", score1: "", score2: "", activity: "", location: "" }];
    setData(prev => ({ ...prev, games }));
    save(games);
  };

  const updateGame = (index, field, value) => {
    const games = [...(data?.games || [])];
    games[index] = { ...games[index], [field]: value };
    setData(prev => ({ ...prev, games }));
  };

  const saveGames = () => save(data?.games || []);

  const removeGame = (index) => {
    const games = (data?.games || []).filter((_, i) => i !== index);
    setData(prev => ({ ...prev, games }));
    save(games);
  };

  const teamBank = data?.team_bank || [];
  const title = division === "boys" ? "Boys Playoffs" : "Girls Playoffs";

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-background">
      <SimpleHeader title={title} onBack={onBack}>
        {isEditable && (
          <Button size="sm" className="h-8 text-xs" onClick={addGame} data-testid="add-game-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Game
          </Button>
        )}
      </SimpleHeader>

      <div className="p-4 max-w-4xl mx-auto space-y-4">
        {/* Team Bank */}
        <Card>
          <CardContent className="pt-3 pb-3">
            <p className="text-[10px] text-muted-foreground mb-2">Team Bank — drag into game slots (ranked by regular season)</p>
            <div className="flex flex-wrap gap-2">
              {teamBank.map(t => {
                const rank = standings.findIndex(s => s.team === t) + 1;
                return (
                  <div key={t} draggable={isEditable}
                    onDragStart={(e) => e.dataTransfer.setData("team", t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white ${isEditable ? "cursor-grab active:cursor-grabbing" : ""}`}
                    style={{ backgroundColor: teamColors[t] || "#666" }}
                    data-testid={`bank-${t.replace(/[^a-zA-Z]/g, "")}`}
                  >
                    {rank > 0 && <span className="bg-white/30 rounded-full px-1.5 mr-1 text-[10px]">#{rank}</span>}
                    {t}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Games */}
        {(data?.games || []).map((game, i) => (
          <Card key={i}>
            <CardContent className="pt-3 pb-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] font-bold text-muted-foreground w-14">Game {i + 1}</span>

                {/* Team 1 drop zone */}
                <div
                  className={`min-w-[120px] h-9 rounded border-2 border-dashed flex items-center justify-center text-xs font-semibold ${game.team1 ? "border-solid" : "border-gray-300"}`}
                  style={game.team1 ? { borderColor: teamColors[game.team1], color: teamColors[game.team1] } : {}}
                  onDragOver={isEditable ? (e) => e.preventDefault() : undefined}
                  onDrop={isEditable ? (e) => { updateGame(i, "team1", e.dataTransfer.getData("team")); saveGames(); } : undefined}
                  data-testid={`game-${i}-team1`}
                >
                  {game.team1 || "Drop Team"}
                </div>

                <span className="text-xs text-muted-foreground">vs.</span>

                {/* Team 2 drop zone */}
                <div
                  className={`min-w-[120px] h-9 rounded border-2 border-dashed flex items-center justify-center text-xs font-semibold ${game.team2 ? "border-solid" : "border-gray-300"}`}
                  style={game.team2 ? { borderColor: teamColors[game.team2], color: teamColors[game.team2] } : {}}
                  onDragOver={isEditable ? (e) => e.preventDefault() : undefined}
                  onDrop={isEditable ? (e) => { updateGame(i, "team2", e.dataTransfer.getData("team")); saveGames(); } : undefined}
                  data-testid={`game-${i}-team2`}
                >
                  {game.team2 || "Drop Team"}
                </div>

                {/* Activity */}
                {isEditable ? (
                  <Input className="w-32 h-7 text-xs" placeholder="Activity" value={game.activity ?? ""}
                    onChange={e => updateGame(i, "activity", e.target.value)} onBlur={saveGames}
                    data-testid={`game-${i}-activity`} />
                ) : game.activity && <span className="text-[10px] text-muted-foreground">{game.activity}</span>}

                {/* Location */}
                {isEditable ? (
                  <Input className="w-28 h-7 text-xs" placeholder="Location" value={game.location ?? ""}
                    onChange={e => updateGame(i, "location", e.target.value)} onBlur={saveGames}
                    data-testid={`game-${i}-location`} />
                ) : game.location && <span className="text-[10px] text-muted-foreground">{game.location}</span>}

                {/* Scores */}
                <div className="flex items-center gap-1">
                  <span className="text-[9px] text-muted-foreground">{game.team1 || "T1"}:</span>
                  {isEditable ? (
                    <Input type="number" className="w-14 h-7 text-xs text-center" value={game.score1 ?? ""}
                      onChange={e => updateGame(i, "score1", e.target.value)} onBlur={saveGames}
                      data-testid={`playoff-score-${i}-1`} />
                  ) : <span className="font-bold text-xs">{game.score1 || "—"}</span>}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[9px] text-muted-foreground">{game.team2 || "T2"}:</span>
                  {isEditable ? (
                    <Input type="number" className="w-14 h-7 text-xs text-center" value={game.score2 ?? ""}
                      onChange={e => updateGame(i, "score2", e.target.value)} onBlur={saveGames}
                      data-testid={`playoff-score-${i}-2`} />
                  ) : <span className="font-bold text-xs">{game.score2 || "—"}</span>}
                </div>

                {isEditable && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => removeGame(i)}>
                    &times;
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {(!data?.games || data.games.length === 0) && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            No playoff games yet. {isEditable && "Click '+ Add Game' to start building the bracket."}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// FINAL STANDINGS
// ═══════════════════════════════════════

function FinalStandingsView({ headers, onBack, lockDivision = null }) {
  const [tab, setTab] = useState(lockDivision || "boys");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const teamColors = tab === "boys" ? TEAM_COLORS_BOYS : TEAM_COLORS_GIRLS;

  const fetchStandings = useCallback(async (div) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/intramurals/${div}/final-standings`, { headers });
      setData(res.data);
    } catch { toast.error("Failed to load standings"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStandings(tab); }, [tab, fetchStandings]);

  const MEDAL = { 1: "text-yellow-500", 2: "text-gray-400", 3: "text-amber-700" };

  return (
    <div className="min-h-screen bg-background">
      <SimpleHeader title="Final Standings" subtitle="Regular Season + Playoffs" onBack={onBack} />

      {!lockDivision && (
      <div className="border-b px-4">
        <div className="flex gap-4">
          <button onClick={() => setTab("boys")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "boys" ? "border-blue-600 text-blue-700" : "border-transparent text-muted-foreground"}`}>Boys</button>
          <button onClick={() => setTab("girls")} className={`py-2 text-xs font-semibold border-b-2 ${tab === "girls" ? "border-rose-600 text-rose-700" : "border-transparent text-muted-foreground"}`}>Girls</button>
        </div>
      </div>
      )}

      {loading ? <div className="text-center py-10 text-muted-foreground">Loading...</div> : data && (
        <div className="p-4 max-w-2xl mx-auto space-y-3">
          {(data.standings || []).map((s) => (
            <Card key={s.team} className="overflow-hidden">
              <div className="flex items-center p-4 gap-4">
                <div className={`text-2xl font-black w-10 text-center ${MEDAL[s.rank] || "text-muted-foreground"}`}>
                  #{s.rank}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold" style={{ color: teamColors[s.team] }}>{s.team}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Season: {s.season_points} &nbsp;|&nbsp; Playoffs: {s.playoff_points}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-black">{s.total_points}</div>
                  <div className="text-[10px] text-muted-foreground">total pts</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// ATHLETICS SHED
// ═══════════════════════════════════════

const CAMP_DAYS_LIST = [
  "2026-06-29","2026-06-30","2026-07-01","2026-07-02","2026-07-07","2026-07-08","2026-07-09","2026-07-10","2026-07-11",
  "2026-07-14","2026-07-15","2026-07-16","2026-07-17","2026-07-18","2026-07-21","2026-07-22","2026-07-23","2026-07-24","2026-07-25",
  "2026-07-28","2026-07-29","2026-07-30","2026-07-31","2026-08-01","2026-08-04","2026-08-05","2026-08-06","2026-08-07","2026-08-08",
  "2026-08-11","2026-08-12","2026-08-13","2026-08-14","2026-08-15","2026-08-18","2026-08-19","2026-08-20",
];

function formatDayLabel(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatDayFull(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function makeEmptyRows() {
  return Array.from({ length: 200 }, () => ({ name: "", equipment: "", timeOut: "", returned: "", timeIn: "" }));
}

function AthleticsShedView({ onBack }) {
  const [selectedDay, setSelectedDay] = useState(CAMP_DAYS_LIST[0]);
  const [rows, setRows] = useState(() => {
    const saved = localStorage.getItem(`shed_${CAMP_DAYS_LIST[0]}`);
    return saved ? JSON.parse(saved) : makeEmptyRows();
  });

  const loadDay = (day) => {
    // Save current
    localStorage.setItem(`shed_${selectedDay}`, JSON.stringify(rows));
    // Load new
    setSelectedDay(day);
    const saved = localStorage.getItem(`shed_${day}`);
    setRows(saved ? JSON.parse(saved) : makeEmptyRows());
  };

  const updateRow = (index, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Auto-save on changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem(`shed_${selectedDay}`, JSON.stringify(rows));
    }, 500);
    return () => clearTimeout(timer);
  }, [rows, selectedDay]);

  const handleClear = () => {
    if (!window.confirm(`Clear all 200 rows for ${formatDayFull(selectedDay)}? This cannot be undone.`)) return;
    const empty = makeEmptyRows();
    setRows(empty);
    localStorage.setItem(`shed_${selectedDay}`, JSON.stringify(empty));
    toast.success("Day cleared");
  };

  const handlePrint = () => {
    const printW = window.open("", "_blank");
    const filledRows = rows.filter(r => r.name || r.equipment || r.timeOut || r.returned || r.timeIn);
    const rowsHtml = (filledRows.length ? filledRows : rows.slice(0, 30)).map((r, i) =>
      `<tr style="border-bottom:1px solid #e5e7eb;${i % 2 === 0 ? "" : "background:#f8fafc"}">
        <td style="padding:4px 8px;text-align:center;font-size:11px">${i + 1}</td>
        <td style="padding:4px 8px;font-size:11px">${r.name}</td>
        <td style="padding:4px 8px;font-size:11px">${r.equipment}</td>
        <td style="padding:4px 8px;font-size:11px">${r.timeOut}</td>
        <td style="padding:4px 8px;font-size:11px">${r.returned}</td>
        <td style="padding:4px 8px;font-size:11px">${r.timeIn}</td>
      </tr>`
    ).join("");
    printW.document.write(`<html><head><title>Athletics Shed — ${formatDayFull(selectedDay)}</title></head><body style="font-family:sans-serif;padding:20px">
      <h2 style="margin:0 0 4px">Athletics Shed — Equipment Sign-Out Log</h2>
      <h3 style="margin:0 0 16px;color:#64748b">${formatDayFull(selectedDay)}</h3>
      <table style="width:100%;border-collapse:collapse;border:1px solid #cbd5e1">
        <thead><tr style="background:#1e3a5f;color:white">
          <th style="padding:6px 8px;font-size:11px;width:30px">#</th>
          <th style="padding:6px 8px;font-size:11px;text-align:left">Name</th>
          <th style="padding:6px 8px;font-size:11px;text-align:left">Equipment Taken</th>
          <th style="padding:6px 8px;font-size:11px;text-align:left">Time Out</th>
          <th style="padding:6px 8px;font-size:11px;text-align:left">Returned</th>
          <th style="padding:6px 8px;font-size:11px;text-align:left">Time In</th>
        </tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`);
    printW.document.close();
    printW.print();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SimpleHeader title="Athletics Shed" subtitle="Equipment Sign-Out Log" onBack={onBack}>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handlePrint} data-testid="shed-print-btn">
          <Printer className="w-3 h-3 mr-1" /> Print
        </Button>
        <Button variant="destructive" size="sm" className="h-8 text-xs" onClick={handleClear} data-testid="shed-clear-btn">
          Clear Day
        </Button>
      </SimpleHeader>

      {/* Day selector */}
      <div className="border-b px-2 py-1.5 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {CAMP_DAYS_LIST.map(day => (
            <button key={day} onClick={() => loadDay(day)}
              className={`px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-colors ${
                selectedDay === day ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
              data-testid={`shed-day-${day}`}
            >
              {formatDayLabel(day)}
            </button>
          ))}
        </div>
      </div>

      {/* Date header */}
      <div className="px-4 py-2 bg-stone-50 border-b">
        <h2 className="text-sm font-bold text-stone-800">Athletics Shed — {formatDayFull(selectedDay)}</h2>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-2 pb-4">
        <table className="w-full border-collapse min-w-[700px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-stone-800 text-white">
              <th className="py-2 px-2 text-[10px] font-semibold w-[40px] text-center">#</th>
              <th className="py-2 px-2 text-[10px] font-semibold text-left w-[20%]">Name</th>
              <th className="py-2 px-2 text-[10px] font-semibold text-left w-[25%]">Equipment Taken</th>
              <th className="py-2 px-2 text-[10px] font-semibold text-left w-[12%]">Time Out</th>
              <th className="py-2 px-2 text-[10px] font-semibold text-left w-[20%]">Returned</th>
              <th className="py-2 px-2 text-[10px] font-semibold text-left w-[12%]">Time In</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={`border-b border-stone-200 ${i % 2 === 0 ? "bg-white" : "bg-stone-50"}`}>
                <td className="py-0.5 px-2 text-[10px] text-center text-stone-400 font-mono">{i + 1}</td>
                <td className="py-0.5 px-1"><input className="w-full h-7 text-[11px] border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded px-1" value={row.name} onChange={e => updateRow(i, "name", e.target.value)} data-testid={`shed-row-${i}-name`} /></td>
                <td className="py-0.5 px-1"><input className="w-full h-7 text-[11px] border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded px-1" value={row.equipment} onChange={e => updateRow(i, "equipment", e.target.value)} /></td>
                <td className="py-0.5 px-1"><input className="w-full h-7 text-[11px] border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded px-1" value={row.timeOut} onChange={e => updateRow(i, "timeOut", e.target.value)} /></td>
                <td className="py-0.5 px-1"><input className="w-full h-7 text-[11px] border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded px-1" value={row.returned} onChange={e => updateRow(i, "returned", e.target.value)} /></td>
                <td className="py-0.5 px-1"><input className="w-full h-7 text-[11px] border-0 bg-transparent focus:outline-none focus:bg-blue-50 rounded px-1" value={row.timeIn} onChange={e => updateRow(i, "timeIn", e.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// ATHLETICS MESSAGES (per-staff threads)
// ═══════════════════════════════════════

function AthleticsMessagesView({ headers, onBack, onLogout }) {
  const [mode, setMode] = useState("list"); // "list" | "thread" | "compose"
  const [staff, setStaff] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [sRes, mRes] = await Promise.all([
        axios.get(`${API}/athletics/staff`, { headers }),
        axios.get(`${API}/athletics/messages`, { headers }),
      ]);
      setStaff(sRes.data || []);
      setMessages(mRes.data || []);
    } catch (e) {
      toast.error("Failed to load messages");
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/athletics/staff/sync`, {}, { headers });
      toast.success(`Synced ${res.data?.imported || 0} staff. ${res.data?.deactivated || 0} marked inactive.`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
  };

  // Aggregate per-staff: count + last message preview
  const threadsByStaff = staff.map(s => {
    const matched = messages.filter(m => {
      if (m.direction === "inbound") return m.staff_person_id === s.person_id;
      // Outbound: check recipients array
      const recips = m.recipients || [];
      return recips.some(r => r.staff_person_id === s.person_id);
    });
    const sorted = matched.slice().sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
    const last = sorted[0];
    const unread = matched.filter(m => m.direction === "inbound" && !m.read).length;
    return { staff: s, count: matched.length, last, unread };
  });

  const filteredThreads = threadsByStaff.filter(t =>
    !search || t.staff.name.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    const at = a.last?.timestamp || "";
    const bt = b.last?.timestamp || "";
    if (at && bt) return bt.localeCompare(at);
    if (at) return -1;
    if (bt) return 1;
    return a.staff.name.localeCompare(b.staff.name);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <SimpleHeader title="Staff Messages" onBack={onBack} onLogout={onLogout} />
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (mode === "compose") {
    return (
      <AthleticsComposeView
        staff={staff}
        headers={headers}
        onBack={() => setMode("list")}
        onLogout={onLogout}
        onSent={async () => { await refresh(); setMode("list"); }}
      />
    );
  }

  if (mode === "thread" && selectedStaff) {
    return (
      <AthleticsThreadView
        staffMember={selectedStaff}
        allMessages={messages}
        headers={headers}
        onBack={() => { setMode("list"); setSelectedStaff(null); }}
        onLogout={onLogout}
        onSent={refresh}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="athletics-messages-view">
      <SimpleHeader title="Staff Messages" subtitle={`${staff.length} athletic staff`} onBack={onBack} onLogout={onLogout}>
        <Button variant="ghost" size="sm" onClick={handleSync} disabled={syncing} data-testid="athletics-msg-sync">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
        </Button>
      </SimpleHeader>
      <div className="px-4 py-4 max-w-2xl mx-auto space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search staff…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="athletics-msg-search"
            />
          </div>
          <Button onClick={() => setMode("compose")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="athletics-msg-compose-btn">
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        </div>

        {filteredThreads.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12">
            {staff.length === 0 ? "No staff found. Tap the refresh icon to sync from CampMinder." : "No matches."}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredThreads.map(t => (
              <button
                key={t.staff.person_id}
                onClick={() => { setSelectedStaff(t.staff); setMode("thread"); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl border bg-white hover:bg-indigo-50 hover:border-indigo-300 transition text-left"
                data-testid={`athletics-msg-staff-${t.staff.person_id}`}
              >
                <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {t.staff.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold truncate">{t.staff.name}</span>
                    {t.last && (
                      <span className="text-[10px] text-muted-foreground flex-shrink-0">
                        {new Date(t.last.timestamp).toLocaleDateString([], { month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground truncate">
                      {t.last ? `${t.last.direction === "inbound" ? "↓ " : "↑ "}${(t.last.message || "").slice(0, 60)}` : "No messages yet"}
                    </p>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {t.unread > 0 && <Badge className="bg-red-500 text-white text-[10px] h-4">{t.unread}</Badge>}
                      {t.count > 0 && <span className="text-[10px] text-muted-foreground">{t.count}</span>}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AthleticsThreadView({ staffMember, allMessages, headers, onBack, onLogout, onSent }) {
  const [reply, setReply] = useState("");
  const [channel, setChannel] = useState("sms");
  const [sending, setSending] = useState(false);

  const thread = allMessages
    .filter(m => {
      if (m.direction === "inbound") return m.staff_person_id === staffMember.person_id;
      return (m.recipients || []).some(r => r.staff_person_id === staffMember.person_id);
    })
    .slice()
    .sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/athletics/messages/send`, {
        staff_ids: [staffMember.person_id],
        channel,
        message: reply.trim(),
        subject: "Athletics — Camper Snapshot",
      }, { headers });
      toast.success(`Sent via ${channel.toUpperCase()}`);
      setReply("");
      if (onSent) await onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="athletics-thread-view">
      <SimpleHeader
        title={staffMember.name}
        subtitle={[staffMember.phone, staffMember.email].filter(Boolean).join(" • ")}
        onBack={onBack}
        onLogout={onLogout}
      />
      <div className="flex-1 overflow-y-auto px-4 py-4 max-w-2xl mx-auto w-full space-y-2">
        {thread.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12">No messages yet. Send the first one below.</div>
        ) : thread.map((m, i) => {
          const isOut = m.direction === "outbound";
          return (
            <div key={m.id || i} className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${isOut ? "bg-indigo-600 text-white" : "bg-stone-100 text-stone-900"}`}>
                <div className="flex items-center gap-1 text-[10px] opacity-70 mb-0.5">
                  {m.channel === "email" ? <Mail className="w-3 h-3" /> : m.channel === "sms" ? <Phone className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                  <span>{m.channel?.toUpperCase()}</span>
                  <span>·</span>
                  <span>{new Date(m.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                </div>
                {m.subject && m.channel === "email" && (
                  <div className="text-[11px] font-semibold mb-1 opacity-90">{m.subject}</div>
                )}
                <div className="text-sm whitespace-pre-wrap break-words">{m.message}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t bg-white p-3 sticky bottom-0">
        <div className="max-w-2xl mx-auto space-y-2">
          <div className="flex items-center gap-2">
            {["sms", "email", "both"].map(c => (
              <button
                key={c}
                onClick={() => setChannel(c)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${channel === c ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-stone-600 border-stone-300"}`}
                data-testid={`athletics-thread-channel-${c}`}
              >
                {c.toUpperCase()}
              </button>
            ))}
            <span className="text-[10px] text-muted-foreground ml-auto">
              {channel !== "email" && !staffMember.phone && "⚠ No phone"}
              {channel !== "sms" && !staffMember.email && "⚠ No email"}
            </span>
          </div>
          <div className="flex items-end gap-2">
            <textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder="Type a message…"
              rows={2}
              className="flex-1 border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="athletics-thread-input"
            />
            <Button
              onClick={handleSend}
              disabled={sending || !reply.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="athletics-thread-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AthleticsComposeView({ staff, headers, onBack, onLogout, onSent }) {
  const [selected, setSelected] = useState(new Set());
  const [channel, setChannel] = useState("sms");
  const [subject, setSubject] = useState("Athletics — Camper Snapshot");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === staff.length) setSelected(new Set());
    else setSelected(new Set(staff.map(s => s.person_id)));
  };

  const filtered = staff.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()));

  const handleSend = async () => {
    if (!message.trim()) { toast.error("Message is required"); return; }
    if (selected.size === 0) { toast.error("Select at least one staff member"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/athletics/messages/send`, {
        staff_ids: Array.from(selected),
        channel,
        message: message.trim(),
        subject: subject.trim() || "Athletics — Camper Snapshot",
      }, { headers });
      toast.success(`Queued to ${res.data?.recipient_count || selected.size} staff via ${channel.toUpperCase()}`);
      if (onSent) await onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="athletics-compose-view">
      <SimpleHeader
        title="New Message"
        subtitle={`${selected.size} of ${staff.length} selected`}
        onBack={onBack}
        onLogout={onLogout}
      />
      <div className="flex-1 overflow-y-auto px-4 py-4 max-w-2xl mx-auto w-full space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search staff…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="athletics-compose-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="athletics-compose-select-all">
            {selected.size === staff.length ? "Clear" : "All"}
          </Button>
        </div>

        <div className="space-y-1 max-h-64 overflow-y-auto border rounded-xl p-2 bg-white">
          {filtered.map(s => (
            <label
              key={s.person_id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer ${selected.has(s.person_id) ? "bg-indigo-50" : "hover:bg-stone-50"}`}
              data-testid={`athletics-compose-row-${s.person_id}`}
            >
              <input
                type="checkbox"
                checked={selected.has(s.person_id)}
                onChange={() => toggle(s.person_id)}
                className="w-4 h-4 accent-indigo-600"
                data-testid={`athletics-compose-check-${s.person_id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {[s.phone, s.email].filter(Boolean).join(" • ") || "No contact info"}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground">Channel:</span>
          {["sms", "email", "both"].map(c => (
            <button
              key={c}
              onClick={() => setChannel(c)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border ${channel === c ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-stone-600 border-stone-300"}`}
              data-testid={`athletics-compose-channel-${c}`}
            >
              {c.toUpperCase()}
            </button>
          ))}
        </div>

        {channel !== "sms" && (
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Subject (email)</label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              data-testid="athletics-compose-subject"
            />
          </div>
        )}

        <div>
          <label className="text-xs font-semibold text-muted-foreground">Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={6}
            placeholder="Type your message…"
            className="w-full border rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="athletics-compose-message"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            SMS includes the standard opt-out footer automatically.
          </p>
        </div>
      </div>
      <div className="border-t bg-white p-3 sticky bottom-0">
        <div className="max-w-2xl mx-auto">
          <Button
            onClick={handleSend}
            disabled={sending || selected.size === 0 || !message.trim()}
            className="w-full bg-indigo-600 hover:bg-indigo-700 h-11"
            data-testid="athletics-compose-send"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Sending…" : `Send to ${selected.size} staff via ${channel.toUpperCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
}



// Export sub-components for use in other dashboards (read-only)
export { TeamSorterView, ScorecardView, PlayoffsView, FinalStandingsView, SimpleHeader };
